import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  ViewChildren
} from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { swapItems } from "src/app/support/array";
import { sntTranslate, translate } from "src/app/support/translate";
import { SidespaceService } from "src/app/services/sidespace.service";
import { ReportDesignerPreviewComponent } from "src/app/modules/bootstrap/modules/report/report-designer/report-designer-preview/report-designer-preview.component";
import { sntConfirm } from "src/app/common/dialog";
import { debounceTime, map, tap } from "rxjs/operators";
import { ItemClickEvent } from "devextreme/ui/button_group";
import {
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent
} from "devextreme/ui/draggable";
import {
  ReportDesignItem,
  ReportDesignItemConfig,
  ReportDesignTablePropertyDataSourceConfig,
  buildItemConfig
} from "src/app/modules/bootstrap/modules/report/report-design-item";

import { sntLog } from "src/app/common/logger";

const logger = sntLog("ReportDesignerComponent");
const DEFAULT_DESIGN_ITEMS_FACTORY: () => Array<
  Partial<ReportDesignItem> & Partial<{ divider: true; title: string }>
> = () => [
  {
    divider: true,
    title: sntTranslate(marker("Layout"))
  },
  {
    icon: "fa-light fa-folders",
    title: sntTranslate(marker("Tab")),
    type: "tab"
  },
  {
    icon: "fa-light fa-object-group",
    title: translate(marker("Gruppo verticale")),
    type: "group-vertical"
  },
  {
    icon: "fa-light fa-block-quote",
    title: sntTranslate(marker("Gruppo orizzontale")),
    type: "group-horizontal"
  },
  {
    divider: true,
    title: sntTranslate(marker("Controlli"))
  },
  {
    icon: "fa-light fa-input-text",
    title: translate(marker("Testo")),
    type: "text"
  },
  {
    icon: "fa-light fa-keyboard",
    title: translate(marker("Area di testo")),
    type: "textarea"
  },
  {
    icon: "fa-light fa-square-check",
    title: translate(marker("Controllo")),
    type: "check"
  },
  {
    icon: "fa-light fa-list",
    title: translate(marker("Lista di controllo")),
    type: "checklist"
  },
  {
    icon: "fa-light fa-list-dropdown",
    title: translate(marker("Casella di selezione")),
    type: "selectbox"
  },
  {
    icon: "fa-light fa-table",
    title: translate(marker("Tabella")),
    type: "table"
  },
  {
    icon: "fa-light fa-text-size",
    title: translate(marker("Titolo")),
    type: "title"
  },
  {
    icon: "fa-light fa-arrows-left-right",
    title: sntTranslate(marker("Spazio vuoto")),
    type: "empty"
  },
  {
    icon: "fa-light fa-file-dashed-line",
    title: translate(marker("Margine vuoto")),
    type: "spacer"
  },
  {
    divider: true,
    title: sntTranslate(marker("Media"))
  },
  {
    icon: "fa-light fa-image",
    title: translate(marker("Immagine")),
    type: "image"
  },
  {
    icon: "fa-light fa-signature",
    title: translate(marker("Firma staff")),
    type: "signature"
  },
  {
    icon: "fa-light fa-file-signature",
    title: sntTranslate(marker("Firma cliente")),
    type: "customer-signature"
  }
];

type ReportDesignerRenderer = "data" | "components";

@Component({
  standalone: false,
  selector: "snt-report-designer",
  templateUrl: "report-designer.component.html",
  styleUrl: "report-designer.component.scss"
})
export class ReportDesignerComponent implements OnInit, OnDestroy {
  @ViewChildren("renderer")
  renderers!: QueryList<any>;

  @Input()
  readonly: boolean = false;

  @Input()
  templateConfig: ReportDesignItemConfig[] = [];

  @Input()
  tablePropertyDataSource: ReportDesignTablePropertyDataSourceConfig = [];

  @Input()
  previewVisible: boolean = true;

  @Input()
  defaultItems: ReportDesignItem[] | undefined;

  @Input()
  renderer: ReportDesignerRenderer = "data";

  @Input()
  headerHTML: string | undefined;

  @Input()
  footerHTML: string | undefined;

  @Input()
  disableRemoveWarning: boolean | undefined;

  @Output()
  templateConfigChange: EventEmitter<ReportDesignItemConfig[]> =
    new EventEmitter();

  @Output()
  rawTemplateConfigChange: EventEmitter<ReportDesignItemConfig[]> =
    new EventEmitter<ReportDesignItemConfig[]>();

  templateConfigChange$: BehaviorSubject<ReportDesignItemConfig[]> =
    new BehaviorSubject<ReportDesignItemConfig[]>([]);

  templateConfigChangesSubscription: Subscription | undefined;

  designItems: Array<
    Partial<ReportDesignItem> & Partial<{ divider: true; title: string }>
  > = DEFAULT_DESIGN_ITEMS_FACTORY();

  readonly template = signal<ReportDesignItemConfig[]>([]);
  readonly previewType = signal<"soap" | "pdf">("soap");

  currentDropTarget: string | undefined;

  private readonly sideSpaceService = inject(SidespaceService);

  private value$: Subject<any> = new Subject<any>();
  private valueSubscription: Subscription | undefined;

  ngOnInit(): void {
    let initial = this.templateConfig || [];

    if (this.defaultItems && this.defaultItems.length > 0) {
      initial = [
        ...initial,
        ...this.defaultItems.map((item) => buildItemConfig(item))
      ];
    }

    this.template.set(initial);

    this.valueSubscription = this.value$
      .pipe(
        tap((value) => {
          this.rawTemplateConfigChange.emit(value);
        }),
        debounceTime(500)
      )
      .subscribe((value) => {
        this.templateConfigChange.emit(value);
        this.templateConfigChange$.next(value);
      });
  }

  ngOnDestroy(): void {
    this.templateConfigChangesSubscription?.unsubscribe();
    this.valueSubscription?.unsubscribe();
  }

  async showPreview(): Promise<void> {
    await this.sideSpaceService.presentAndOpen(
      ReportDesignerPreviewComponent,
      {
        templateConfigChange$: this.templateConfigChange$,
        headerHTML: this.headerHTML,
        footerHTML: this.footerHTML
      },
      {
        size: "md"
      }
    );
    this.templateConfigChange$.next(this.templateConfigChange$.value);
  }

  onEnableHideFeatureChange(
    templateItem: ReportDesignItemConfig,
    value: boolean
  ): void {
    templateItem.config.hidden = value;
    this.notifyRootSignal();
    this.notifyTemplateChange();
  }

  onTemplateItemDragStart(dxEvent: {
    cancel: boolean;
    element: HTMLElement;
    itemData: any;
    fromData: any;
    fromIndex: number;
  }): void {
    const templateItemConfig = this.template()[dxEvent.fromIndex];

    if (templateItemConfig.disableSortable) {
      dxEvent.cancel = true;
      return;
    }

    dxEvent.itemData = dxEvent.fromData;
  }

  onTemplateItemDrop(dxEvent: { fromIndex: number; toIndex: number }): void {
    logger.debug("[ReportDesigner] notifyTemplate by onTemplateItemDrop");
    const current = [...this.template()];
    swapItems(current, dxEvent.fromIndex, dxEvent.toIndex);
    this.template.set(current);
    this.notifyTemplateChange();
  }

  onDragStart(dxEvent: DragStartEvent, item: ReportDesignItemConfig): void {
    if (this.readonly) {
      dxEvent.cancel = true;
      return;
    }

    if (item.canAdd && !item.canAdd(this.template())) {
      dxEvent.cancel = true;
    }
  }

  onDragMove(dxEvent: DragMoveEvent): void {
    this.currentDropTarget = dxEvent?.toData?.target;
  }

  async onDragEnd(dxEvent: DragEndEvent): Promise<void> {
    this.currentDropTarget = undefined;

    if (!!dxEvent.toData && !!dxEvent.toData.target) {
      const item = buildItemConfig(dxEvent.fromData);

      dxEvent.toData.templateItemsTarget.push(item);

      this.notifyRootSignal();
      this.notifyTemplateChange();
    }
  }

  onSwapUp(
    template: ReportDesignItemConfig[],
    index: number,
    templateArrayOwner?: Record<string, ReportDesignItemConfig[] | unknown>,
    templateArrayKey?: string
  ): void {
    swapItems(template, index, index - 1);
    this.replaceTemplateArrayReference(
      template,
      templateArrayOwner,
      templateArrayKey
    );
  }

  onSwapDown(
    template: ReportDesignItemConfig[],
    index: number,
    templateArrayOwner?: Record<string, ReportDesignItemConfig[] | unknown>,
    templateArrayKey?: string
  ): void {
    swapItems(template, index, index + 1);
    this.replaceTemplateArrayReference(
      template,
      templateArrayOwner,
      templateArrayKey
    );
  }

  private notifyRootSignal(): void {
    this.template.set([...this.template()]);
  }

  private replaceTemplateArrayReference(
    template: ReportDesignItemConfig[],
    templateArrayOwner?: Record<string, ReportDesignItemConfig[] | unknown>,
    templateArrayKey?: string
  ): void {
    if (template === this.template()) {
      this.template.set([...this.template()]);
    } else if (templateArrayOwner && templateArrayKey) {
      templateArrayOwner[templateArrayKey] = [...template];
      this.template.set([...this.template()]);
    }
  }

  async onRemoveItem(
    templateItemsTarget: ReportDesignItemConfig[],
    item: ReportDesignItemConfig
  ): Promise<void> {
    if (!this.disableRemoveWarning) {
      const result = await sntConfirm(
        sntTranslate(marker("Editor schede visita")),
        sntTranslate(
          marker(
            "La rimozione campo comporterà la perdita dei valori inseriti nelle schede visita passate. Vuoi proseguire?"
          )
        )
      );

      if (!result) {
        return;
      }
    }

    const index = templateItemsTarget.indexOf(item);
    templateItemsTarget.splice(index, 1);

    this.notifyRootSignal();
    this.notifyTemplateChange();
  }

  onConfigChanged(templateItem: ReportDesignItemConfig, config: any): void {
    templateItem.config = config;
    this.notifyRootSignal();
    this.notifyTemplateChange();
  }

  notifyTemplateChange(): void {
    this.value$.next(this.template());
  }

  togglePreview(): void {
    this.previewVisible = !this.previewVisible;
  }

  onPreviewTypeChange(event: ItemClickEvent): void {
    const type = event.itemIndex === 0 ? "soap" : "pdf";
    this.previewType.set(type);
  }

  onItemMouseEnter(
    item: ReportDesignItemConfig,
    containerElementRef: HTMLElement
  ): void {}

  onItemMouseLeave(
    item: ReportDesignItemConfig,
    containerElementRef: HTMLElement
  ): void {}
}
